import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Loader2, XCircle, Ticket } from 'lucide-react';

export default function EventCheckoutSuccess() {
  const { eid } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('verifying');   // verifying | ok | error
  const [ticket, setTicket] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState('error'); setErrMsg('Missing Stripe session — please try again.'); return; }
    (async () => {
      try {
        const r = await evtApi.verifyCheckout(eid, sessionId);
        setTicket(r.ticket);
        await refresh();
        setState('ok');
      } catch (err) {
        const raw = err?.response?.data?.detail || err?.message || '';
        // If the upstream server or CDN returned an HTML body (Cloudflare 502
        // etc.), don't leak that to the user — show a stable message instead.
        const looksLikeHtml = typeof raw === 'string' && (raw.includes('<') || raw.length > 200);
        setErrMsg(looksLikeHtml
          ? 'We couldn\'t verify your payment. Please contact support with your Stripe session ID and we\'ll sort it out.'
          : raw || 'Verification failed.');
        setState('error');
      }
    })();
    // eslint-disable-next-line
  }, [eid, sessionId]);

  return (
    <div className="max-w-md mx-auto pbb-card p-8 text-center space-y-4" data-testid="checkout-success-page">
      {state === 'verifying' && (
        <>
          <Loader2 className="mx-auto animate-spin text-green-400" size={30}/>
          <div className="font-semibold text-white">Confirming your payment…</div>
          <div className="text-xs text-gray-400">Do not close this tab — we're finalizing your ticket.</div>
        </>
      )}
      {state === 'ok' && ticket && (
        <>
          <CheckCircle2 className="mx-auto text-green-400" size={40}/>
          <div className="font-bold text-white text-xl">Payment confirmed 🎟️</div>
          <div className="text-sm text-gray-300">
            Your ticket for <b>{ticket.eventTitle}</b> is booked.<br/>
            An email and SMS with your QR are on the way.
          </div>
          <div className="pt-2">
            <div className="text-[10px] tracking-widest text-gray-500 uppercase">Ticket ID</div>
            <div className="font-mono font-bold text-white tracking-widest">#{ticket.ticketId}</div>
          </div>
          <Link to={`/tickets/${ticket.ticketId}`} data-testid="checkout-view-ticket"
            className="pbb-btn-green mt-3 inline-flex items-center gap-2">
            <Ticket size={14}/> View my ticket
          </Link>
        </>
      )}
      {state === 'error' && (
        <>
          <XCircle className="mx-auto text-red-500" size={36}/>
          <div className="font-semibold text-white">Something went wrong</div>
          <div className="text-sm text-gray-400">{errMsg}</div>
          <Link to="/events" className="text-green-400 text-sm mt-2 inline-block">Back to events</Link>
        </>
      )}
    </div>
  );
}
